"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {formatDateTime, formatTime} from "@/libs/helper";
import {ManageBookingItem} from "@/types/booking";
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader
} from "@react-google-maps/api";
import {useMemo, useState} from "react";
import {FiCalendar, FiMapPin, FiUser} from "react-icons/fi";
import {PiBusLight} from "react-icons/pi";
import {getBookingUiDetail} from "./manageBooking.shared";

type BookingMapModalContentProps = {
  booking: ManageBookingItem;
  routeName: string;
  mode: "live" | "map";
};

const MAP_DEFAULT_CENTER = {lat: 11.0168, lng: 76.9558};

const getBookingMapPoints = (booking: ManageBookingItem) => {
  const seed = Number(booking.bookingId || 0);
  const baseLat = MAP_DEFAULT_CENTER.lat + ((seed % 7) - 3) * 0.01;
  const baseLng = MAP_DEFAULT_CENTER.lng + ((seed % 5) - 2) * 0.012;

  return {
    start: {lat: baseLat, lng: baseLng},
    end: {
      lat: baseLat + 0.028,
      lng: baseLng + 0.034
    },
    path: [
      {lat: baseLat, lng: baseLng},
      {lat: baseLat + 0.008, lng: baseLng + 0.009},
      {lat: baseLat + 0.015, lng: baseLng + 0.018},
      {lat: baseLat + 0.021, lng: baseLng + 0.026},
      {lat: baseLat + 0.028, lng: baseLng + 0.034}
    ]
  };
};

const BookingMapModalContent = ({
  booking,
  routeName,
  mode
}: BookingMapModalContentProps) => {
  const mapKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
  const {isLoaded} = useJsApiLoader({googleMapsApiKey: mapKey});
  const mapPoints = useMemo(() => getBookingMapPoints(booking), [booking]);

  return (
    <div className="flex h-[72vh] min-h-[520px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          {mode === "live" ? "Live Tracking" : "View on Map"}
        </div>
        <div className="mt-1 text-lg font-semibold text-slate-800">
          {booking.vehicleName} - {routeName}
        </div>
        <div className="mt-1 text-sm text-slate-500">
          {booking.pickupPoint} {"->"} {booking.dropPoint}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-auto p-5">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-800">
              {mode === "live" ? "Live route preview" : "Route preview"}
            </div>
            <div className="text-xs text-slate-500">
              {formatTime(booking.bookingTime)} -{" "}
              {formatTime(booking.bookingTime + 90 * 60 * 1000)}
            </div>
          </div>

          {isLoaded ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <GoogleMap
                mapContainerStyle={{width: "100%", height: "430px"}}
                center={mapPoints.start}
                zoom={13}
                options={{
                  fullscreenControl: true,
                  streetViewControl: false,
                  mapTypeControl: false
                }}
              >
                <Polyline
                  path={mapPoints.path}
                  options={{
                    strokeColor: mode === "live" ? "#059669" : "#2563EB",
                    strokeOpacity: 0.9,
                    strokeWeight: 4
                  }}
                />

                <Marker
                  position={mapPoints.start}
                  title={booking.pickupPoint}
                  label={{
                    text: "S",
                    color: "#ffffff",
                    fontSize: "12px",
                    fontWeight: "700"
                  }}
                />

                <Marker
                  position={mapPoints.end}
                  title={booking.dropPoint}
                  label={{
                    text: "E",
                    color: "#ffffff",
                    fontSize: "12px",
                    fontWeight: "700"
                  }}
                />
              </GoogleMap>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
              Google map is loading...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const Card = ({
  b,
  statusType
}: {
  b: ManageBookingItem;
  statusType?: number;
}) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const uiDetail = useMemo(
    () => getBookingUiDetail(b, statusType),
    [b, statusType]
  );
  const isOngoing = statusType === 1;
  const isUpcoming = statusType === 2;
  const seatFillPercentage = Math.min(
    100,
    Math.round((uiDetail.seatsFilled / uiDetail.totalSeats) * 100)
  );
  const mapModalMode = isOngoing ? "live" : "map";

  return (
    <>
      <div className="h-64 w-full min-w-0 rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="truncate font-bold leading-none tracking-tight text-slate-900">
                {b.vehicleName}
              </div>
              <div className="mt-1 truncate text-xs font-medium text-slate-500">
                {uiDetail.routeName}
              </div>
            </div>

            <div className="w-32 shrink-0">
              <div className="mt-2 text-right text-xs font-medium text-slate-500">
                <span className="font-semibold text-slate-700">
                  {uiDetail.seatsFilled}/{uiDetail.totalSeats}
                </span>{" "}
                Seats Filled
              </div>
              <div className="mt-1.5 flex justify-end">
                <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{width: `${seatFillPercentage}%`}}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[18px] bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-900">
            {formatTime(b.bookingTime)} -{" "}
            {formatTime(b.bookingTime + 90 * 60 * 1000)}
          </div>

          <div className="relative space-y-2 pl-9">
            <div className="absolute left-[11px] top-5 h-[30px] w-0.5 border-l-2 border-dotted border-slate-200" />
            <div className="relative">
              <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-50">
                <FiMapPin className="h-3 w-3 text-blue-500" />
              </span>
              <div className="truncate text-sm font-medium text-slate-900">
                {b.pickupPoint}
              </div>
            </div>
            <div className="relative">
              <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50">
                <FiMapPin className="h-3 w-3 text-emerald-500" />
              </span>
              <div className="truncate text-sm font-medium text-slate-900">
                {b.dropPoint}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <FiCalendar className="h-3.5 w-3.5 text-slate-500" />
                <span>{formatDateTime(b.bookingTime).split(",")[0]}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiUser className="h-3.5 w-3.5 text-slate-500" />
                <span>{b.driverName}</span>
              </div>
              <div className="flex items-center gap-2">
                <PiBusLight className="h-3.5 w-3.5 text-slate-500" />
                <span>{b.driverEmpId}</span>
              </div>
            </div>
          </div>

          <div className="grid gap-2 xl:grid-cols-2">
            {isUpcoming ? (
              <>
                <button
                  className="rounded-[18px] border border-rose-200 bg-white px-4 py-2.5 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="rounded-[18px] border border-slate-200 bg-white px-1 py-2.5 text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
                  onClick={() => setIsMapOpen(true)}
                  type="button"
                >
                  View on Map
                </button>
              </>
            ) : (
              <>
                {isOngoing ? (
                  <button
                    className={`rounded-[18px] px-2 py-2 text-xs font-semibold text-white transition-colors ${
                      uiDetail.liveTrackingAvailable
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "cursor-not-allowed bg-slate-400"
                    }`}
                    disabled={!uiDetail.liveTrackingAvailable}
                    onClick={() => setIsMapOpen(true)}
                    type="button"
                  >
                    Track Live
                  </button>
                ) : (
                  <button
                    className="rounded-[18px] border border-slate-200 bg-white px-1 py-2.5 text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
                    onClick={() => setIsMapOpen(true)}
                    type="button"
                  >
                    View on Map
                  </button>
                )}
                <button
                  className="rounded-[18px] border border-slate-200 bg-white px-2 py-2.5 text-center text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
                  onClick={() => setIsDetailOpen(true)}
                  type="button"
                >
                  ViewDetails
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <NeoModal
        actionType="manageBookingDetailView"
        onOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        hasActions={false}
        data={{
          booking: b,
          statusType: statusType ?? 1
        }}
      />

      <NeoModal
        actionType="custom"
        onOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        hasActions={false}
        customContent={
          <BookingMapModalContent
            booking={b}
            routeName={uiDetail.routeName}
            mode={mapModalMode}
          />
        }
      />
    </>
  );
};
